// src/pages/consulting/ConsultingServices.js
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ConsultingLayout from "./ConsultingLayout.js";
import {
  Sparkles, Zap, Users, Database, Award, ArrowRight, TrendingUp, ChevronRight, Check,
} from "lucide-react";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Bricolage+Grotesque:wght@400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .cs-page {
    --bg:        #04050a;
    --surface:   #080b12;
    --surface-2: #0d1018;
    --border:    rgba(255,255,255,0.06);
    --border-2:  rgba(255,255,255,0.10);
    --accent:    #6366f1;
    --accent-2:  #8b5cf6;
    --white:     #f8fafc;
    --muted:     #475569;
    --muted-2:   #64748b;
    --text:      #cbd5e1;

    font-family: 'Inter', sans-serif;
    background: var(--bg);
    color: var(--white);
    min-height: 100vh;
    padding-bottom: 80px;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }

  /* ── Background ── */
  .cs-bg-canvas {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  .cs-bg-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(99,102,241,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(99,102,241,0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%);
  }

  .cs-bg-orb {
    position: absolute;
    top: -200px; left: 50%;
    transform: translateX(-50%);
    width: 700px; height: 500px;
    background: radial-gradient(ellipse, rgba(99,102,241,0.08) 0%, transparent 70%);
    animation: orb-pulse 8s ease-in-out infinite;
  }

  @keyframes orb-pulse {
    0%, 100% { opacity: 1; transform: translateX(-50%) scale(1); }
    50%       { opacity: 0.6; transform: translateX(-50%) scale(1.06); }
  }

  /* ── Shell ── */
  .cs-shell {
    position: relative;
    z-index: 1;
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 32px;
  }

  /* ── Header ── */
  .cs-header {
    padding: 64px 0 56px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    animation: fade-up 0.6s ease both;
  }

  .cs-header-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--muted-2);
    margin-bottom: 20px;
  }

  .cs-header-tag-line {
    width: 20px; height: 1px;
    background: var(--muted);
  }

  .cs-h1 {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: clamp(2.2rem, 5vw, 3.8rem);
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: -0.04em;
    margin-bottom: 18px;
    color: var(--white);
  }

  .cs-h1 span {
    background: linear-gradient(135deg, #a5b4fc 0%, #818cf8 40%, #6366f1 70%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .cs-header-desc {
    color: var(--muted-2);
    max-width: 420px;
    line-height: 1.65;
    font-size: 15px;
  }

  /* ── Grid ── */
  .cs-grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 14px;
  }

  /* ── Card ── */
  .cs-card {
    grid-column: span 4;
    border-radius: 20px;
    position: relative;
    border: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 280px;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    background: var(--surface);
  }

  .cs-card:hover {
    border-color: rgba(99,102,241,0.25);
    transform: translateY(-4px);
    box-shadow: 0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.1);
  }

  .cs-card.is-open {
    grid-column: span 8;
    transform: none;
    border-color: rgba(99,102,241,0.3);
    box-shadow: 0 0 0 1px rgba(99,102,241,0.15), 0 32px 80px rgba(0,0,0,0.5);
  }

  /* Top shine line */
  .cs-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99,102,241,0.5), transparent);
    opacity: 0;
    transition: opacity 0.3s;
    z-index: 3;
  }

  .cs-card:hover::before,
  .cs-card.is-open::before { opacity: 1; }

  /* ── Background image ── */
  .cs-card-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    transition: transform 0.6s cubic-bezier(0.16,1,0.3,1);
  }

  .cs-card:hover .cs-card-bg { transform: scale(1.04); }
  .cs-card.is-open .cs-card-bg { transform: scale(1.02); }

  .cs-card-bg img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: brightness(0.18) saturate(0.5);
    transition: filter 0.4s;
  }

  .cs-card:hover .cs-card-bg img { filter: brightness(0.22) saturate(0.6); }

  .cs-card-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(160deg, rgba(8,11,18,0.2) 0%, rgba(8,11,18,0.97) 75%);
    z-index: 1;
  }

  /* ── Card content ── */
  .cs-card-content {
    position: relative;
    z-index: 2;
    padding: 28px;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* ── Icon ── */
  .cs-icon-box {
    width: 42px; height: 42px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(99,102,241,0.12);
    border: 1px solid rgba(99,102,241,0.2);
    color: #a5b4fc;
    flex-shrink: 0;
    transition: all 0.3s;
    margin-bottom: 16px;
  }

  .cs-card:hover .cs-icon-box,
  .cs-card.is-open .cs-icon-box {
    background: rgba(99,102,241,0.2);
    border-color: rgba(99,102,241,0.35);
    box-shadow: 0 0 20px rgba(99,102,241,0.2);
  }

  /* ── Texts ── */
  .cs-card-top { flex: 1; }

  .cs-card-title {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--white);
    margin-bottom: 8px;
  }

  .cs-card-desc {
    font-size: 13.5px;
    color: var(--muted-2);
    line-height: 1.6;
  }

  /* ── Click hint ── */
  .cs-card-hint {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--accent);
    margin-top: 16px;
    opacity: 0;
    transform: translateY(4px);
    transition: all 0.25s;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .cs-card:hover .cs-card-hint { opacity: 1; transform: translateY(0); }
  .cs-card.is-open .cs-card-hint { opacity: 0; }

  /* ── Expansion ── */
  .cs-expansion {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid rgba(255,255,255,0.07);
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 24px;
    align-items: end;
    animation: fade-up 0.35s ease both;
  }

  @keyframes fade-up {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .cs-bullets {
    display: flex;
    flex-direction: column;
    gap: 8px;
    list-style: none;
  }

  .cs-bullet {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: var(--text);
    padding: 9px 14px;
    background: rgba(255,255,255,0.03);
    border: 1px solid var(--border);
    border-radius: 8px;
    transition: all 0.2s;
  }

  .cs-bullet:hover {
    background: rgba(99,102,241,0.07);
    border-color: rgba(99,102,241,0.18);
    color: var(--white);
  }

  .cs-bullet-check {
    width: 18px; height: 18px;
    border-radius: 50%;
    background: rgba(16,185,129,0.12);
    border: 1px solid rgba(16,185,129,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #10b981;
  }

  /* ── CTA small ── */
  .cs-cta-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 20px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    border: 1px solid rgba(139,92,246,0.3);
    box-shadow: 0 0 24px rgba(99,102,241,0.25);
    white-space: nowrap;
    transition: all 0.25s ease;
  }

  .cs-cta-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 40px rgba(99,102,241,0.4);
  }

  /* ── Number badge ── */
  .cs-card-num {
    position: absolute;
    top: 20px; right: 20px;
    font-size: 11px;
    font-weight: 700;
    color: var(--muted);
    letter-spacing: 0.06em;
    z-index: 3;
    transition: color 0.3s;
  }

  .cs-card:hover .cs-card-num,
  .cs-card.is-open .cs-card-num { color: rgba(99,102,241,0.7); }

  /* ── Bottom CTA strip ── */
  .cs-footer-strip {
    margin-top: 48px;
    border-radius: 20px;
    padding: 48px 56px;
    background: var(--surface);
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    position: relative;
    overflow: hidden;
    animation: fade-up 0.6s 0.3s ease both;
  }

  .cs-footer-strip::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 70% 80% at 0% 50%, rgba(99,102,241,0.07) 0%, transparent 70%);
    pointer-events: none;
  }

  .cs-footer-strip::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99,102,241,0.4), transparent);
  }

  .cs-footer-text h3 {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: var(--white);
    margin-bottom: 8px;
  }

  .cs-footer-text p {
    font-size: 14px;
    color: var(--muted-2);
    line-height: 1.6;
    max-width: 380px;
  }

  .cs-footer-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 14px 28px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    border: 1px solid rgba(139,92,246,0.3);
    box-shadow: 0 0 30px rgba(99,102,241,0.25), 0 4px 16px rgba(0,0,0,0.3);
    white-space: nowrap;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
    transition: all 0.25s ease;
  }

  .cs-footer-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 50px rgba(99,102,241,0.4), 0 8px 24px rgba(0,0,0,0.4);
  }

  /* ── Scroll Navigator ── */
  .cs-scroll-nav {
    position: fixed;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .cs-scroll-btn {
    width: 40px; height: 40px;
    border-radius: 12px;
    background: rgba(13,16,24,0.85);
    border: 1px solid rgba(255,255,255,0.08);
    backdrop-filter: blur(12px);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s ease;
    color: rgba(255,255,255,0.5);
    outline: none;
    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  }

  .cs-scroll-btn:hover {
    background: rgba(99,102,241,0.15);
    border-color: rgba(99,102,241,0.35);
    color: #818cf8;
    transform: scale(1.08);
    box-shadow: 0 0 20px rgba(99,102,241,0.2), 0 4px 20px rgba(0,0,0,0.4);
  }

  .cs-scroll-btn.disabled { opacity: 0.25; pointer-events: none; }

  .cs-scroll-btn svg {
    width: 16px; height: 16px;
    stroke: currentColor;
    stroke-width: 2.2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .cs-scroll-track {
    width: 2px; height: 80px;
    background: rgba(255,255,255,0.06);
    border-radius: 2px;
    overflow: hidden;
    position: relative;
  }

  .cs-scroll-fill {
    width: 100%;
    background: linear-gradient(180deg, #6366f1, #8b5cf6);
    border-radius: 2px;
    transition: height 0.1s linear;
    box-shadow: 0 0 8px rgba(99,102,241,0.6);
    position: absolute;
    top: 0; left: 0;
  }

  /* ── Responsive ── */
  @media (max-width: 900px) {
    .cs-card          { grid-column: span 6; }
    .cs-card.is-open  { grid-column: span 12; }
  }

  @media (max-width: 600px) {
    .cs-card, .cs-card.is-open { grid-column: span 12; min-height: 220px; }
    .cs-footer-strip { flex-direction: column; padding: 36px 28px; text-align: center; }
    .cs-footer-text p { max-width: 100%; }
    .cs-expansion { grid-template-columns: 1fr; }
    .cs-scroll-nav { display: none; }
  }
`;

/* ── Scroll Navigator ── */
function ScrollNavigator() {
  const [progress, setProgress] = React.useState(0);
  const [atTop, setAtTop]       = React.useState(true);
  const [atBottom, setAtBottom] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => {
      const scrollTop  = window.scrollY;
      const docHeight  = document.documentElement.scrollHeight - window.innerHeight;
      const pct        = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(pct);
      setAtTop(scrollTop <= 10);
      setAtBottom(pct >= 98);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="cs-scroll-nav">
      <button
        className={`cs-scroll-btn ${atTop ? "disabled" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <svg viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"/></svg>
      </button>
      <div className="cs-scroll-track">
        <div className="cs-scroll-fill" style={{ height: `${progress}%` }}/>
      </div>
      <button
        className={`cs-scroll-btn ${atBottom ? "disabled" : ""}`}
        onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" })}
      >
        <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
    </div>
  );
}

/* ── Main ── */
export default function ConsultingServices() {
  const [openId, setOpenId] = useState(null);

  const services = useMemo(() => [
    {
      id: "digital-si",
      title: "Transformation digitale",
      desc: "Architecturer le futur numérique de votre entreprise.",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=800&auto=format",
      icon: Sparkles,
      bullets: ["Audit SI complet", "Roadmap stratégique", "Gouvernance IT"],
    },
    {
      id: "amoa-pilotage",
      title: "AMOA & Pilotage",
      desc: "La maîtrise d'ouvrage au service de vos résultats.",
      image: "https://images.unsplash.com/photo-1507206130118-b5907f817163?q=80&w=800&auto=format",
      icon: Zap,
      bullets: ["Recueil de besoins", "Planning & RACI", "Gestion des risques"],
    },
    {
      id: "integration-erp",
      title: "Intégration ERP",
      desc: "Connectez vos métiers à travers un outil unique.",
      image: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1000&auto=format",
      icon: Database,
      bullets: ["Spécifications", "Pilotage UAT", "Migration Data"],
    },
    {
      id: "data-bi",
      title: "Data & BI",
      desc: "Transformez vos données en décisions stratégiques.",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=800&auto=format",
      icon: TrendingUp,
      bullets: ["Gouvernance data", "Dashboards PowerBI", "Qualité de données"],
    },
    {
      id: "change-mgmt",
      title: "Change Management",
      desc: "L'humain au cœur de votre transformation SI.",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=800&auto=format",
      icon: Users,
      bullets: ["Plan d'adoption", "Ateliers formation", "Coaching"],
    },
    {
      id: "sourcing-it",
      title: "Expertise & Sourcing",
      desc: "Trouvez les talents qui feront la différence.",
      image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=800&auto=format",
      icon: Award,
      bullets: ["Sourcing d'experts", "Vetting technique", "Matching culture"],
    },
  ], []);

  return (
    <ConsultingLayout>
      <style>{STYLES}</style>
      <ScrollNavigator />

      <main className="cs-page">

        {/* Background */}
        <div className="cs-bg-canvas">
          <div className="cs-bg-grid"/>
          <div className="cs-bg-orb"/>
        </div>

        <div className="cs-shell">

          {/* ── Header ── */}
          <header className="cs-header">
            <div className="cs-header-tag">
              <span className="cs-header-tag-line"/>
              Expertises
            </div>
            <h1 className="cs-h1">
              Nos Services <span>Consulting.</span>
            </h1>
            <p className="cs-header-desc">
              Expertise technique et vision métier pour vos projets
              de transformation digitale.
            </p>
          </header>

          {/* ── Grid ── */}
          <div className="cs-grid">
            {services.map((s, idx) => {
              const isOpen = openId === s.id;
              const Icon   = s.icon;

              return (
                <article
                  key={s.id}
                  className={`cs-card ${isOpen ? "is-open" : ""}`}
                  onClick={() => setOpenId(isOpen ? null : s.id)}
                >
                  {/* Number */}
                  <span className="cs-card-num">
                    {String(idx + 1).padStart(2, "0")}
                  </span>

                  {/* BG image */}
                  <div className="cs-card-bg">
                    <img src={s.image} alt="" loading="lazy"/>
                  </div>
                  <div className="cs-card-overlay"/>

                  {/* Content */}
                  <div className="cs-card-content">
                    <div className="cs-icon-box">
                      <Icon size={18}/>
                    </div>

                    <div className="cs-card-top">
                      <h3 className="cs-card-title">{s.title}</h3>
                      <p className="cs-card-desc">{s.desc}</p>
                    </div>

                    {/* Hover hint */}
                    <span className="cs-card-hint">
                      Voir le détail <ChevronRight size={13}/>
                    </span>

                    {/* Expansion */}
                    {isOpen && (
                      <div className="cs-expansion">
                        <ul className="cs-bullets">
                          {s.bullets.map(b => (
                            <li className="cs-bullet" key={b}>
                              <span className="cs-bullet-check">
                                <Check size={10}/>
                              </span>
                              {b}
                            </li>
                          ))}
                        </ul>
                        <Link
                          to="/consulting/contact"
                          className="cs-cta-btn"
                          onClick={e => e.stopPropagation()}
                        >
                          Détails <ArrowRight size={14}/>
                        </Link>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {/* ── Footer CTA strip ── */}
          <div className="cs-footer-strip">
            <div className="cs-footer-text">
              <h3>Un projet en tête ?</h3>
              <p>
                Parlons de vos enjeux lors d'un premier échange stratégique,
                sans engagement. Nos experts sont disponibles sous 48h.
              </p>
            </div>
            <Link to="/consulting/contact" className="cs-footer-btn">
              Démarrer un projet <ArrowRight size={15}/>
            </Link>
          </div>

        </div>
      </main>




    </ConsultingLayout>
  );
}
